import { Typography } from '@/components/atoms/Typography';
import { CancelAppointmentIcon } from '@/components/atoms/icons';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useUIColors } from '@/core/hooks';
import React, { useEffect, useRef } from 'react';
import { Modal, StyleSheet, View } from 'react-native';

export interface AppointmentCanceledDialogProps {
    visible: boolean;
    /** Title text; defaults to "Appointment Canceled" */
    title?: string;
    /** Description text */
    message?: string;
    /** Auto-close delay in ms; defaults to 3000 */
    autoCloseDelayMs?: number;
    onAutoClose: () => void;
}

export const AppointmentCanceledDialog: React.FC<Readonly<AppointmentCanceledDialogProps>> = ({
    visible,
    title = 'Appointment Canceled',
    message = 'Your appointment has been canceled successfully.',
    autoCloseDelayMs = 3000,
    onAutoClose,
}) => {
    const colors = useUIColors();
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Auto-close behaviour
    useEffect(() => {
        if (!visible) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            return;
        }

        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            onAutoClose();
        }, autoCloseDelayMs);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [visible, autoCloseDelayMs, onAutoClose]);

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onAutoClose}>
            <View style={styles.overlay}>
                <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
                    {/* Icon */}
                    <View style={styles.iconContainer}>
                        <CancelAppointmentIcon size={48} color={colors.fontPrimary} />
                    </View>

                    {/* Title */}
                    <Typography
                        variant="headline"
                        color="primary"
                        style={styles.title}
                    >
                        {title}
                    </Typography>

                    {/* Description */}
                    <Typography
                        variant="body"
                        color="secondary"
                        style={styles.message}
                    >
                        {message}
                    </Typography>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: UI_PRIMITIVES.overlayBackdrop,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
    },
    container: {
        borderRadius: 16,
        paddingVertical: 32,
        paddingHorizontal: 24,
        width: '100%',
        maxWidth: 340,
        alignItems: 'center',
        gap: 16,
    },
    iconContainer: {
        marginBottom: 4,
    },
    title: {
        textAlign: 'center',
        width: '100%',
    },
    message: {
        textAlign: 'center',
        fontSize: 14,
        lineHeight: 21,
        width: '100%',
    },
});

export default AppointmentCanceledDialog;
